// src/App.js
import React, { useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import ForgotPasswordForm from './components/auth/ForgotPasswordForm';
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import ChangePassword from './pages/ChangePassword';
import ProductManagement from './pages/ProductManagement';
import CustomerManagement from './pages/CustomerManagement';
import InvoiceManagement from './pages/InvoiceManagement';
import GenericPage from './pages/GenericPage';
import { useNavigation } from './hooks/useNavigation';
import './index.css';
import ExpensesManagement from './pages/ExpensesManagement';
import MenuManagement from './pages/MenuManagement';
import UserManagement from './pages/UserManagement';
import PermissionManagement from './pages/PermissionManagement';
import DynamicSidebar from './components/layout/DynamicSidebar';
import RoleManagement from './pages/RoleManagement';

import ExpensesTrack from './pages/ExpenseTrack';

import Reports from './pages/Reports';
import ProfitLossReport from './pages/ProfitLossReport';

// Loading Component
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

// Auth Flow Component
const AuthFlow = () => {
  const [currentPage, setCurrentPage] = useState('login');

  switch (currentPage) {
    case 'register':
      return <RegisterForm setCurrentPage={setCurrentPage} />;
    case 'forgot-password':
      return <ForgotPasswordForm setCurrentPage={setCurrentPage} />;
    default:
      return <LoginForm setCurrentPage={setCurrentPage} />;
  }
};

// Main Dashboard Layout
const DashboardLayout = () => {
  const { 
    currentPage, 
    sidebarOpen, 
    expandedMenus, 
    setSidebarOpen, 
    handleMenuClick 
  } = useNavigation();

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard setCurrentPage={handleMenuClick} />;
      case 'profile':
        return <Profile setCurrentPage={handleMenuClick} />;
      case 'change-password':
        return <ChangePassword setCurrentPage={handleMenuClick} />;
      case 'users':
        return <UserManagement/>;
      case 'roles':
        return <RoleManagement/>;
      case 'appmenu':
        return <MenuManagement/>;
      case 'permissions':
        return <PermissionManagement/>;
      case 'customers':
        return <CustomerManagement />;
      case 'products':
        return <ProductManagement />;
      case 'expenses':
        return <ExpensesManagement />;
      case 'trackexpenses':
        return <ExpensesTrack/>;
      case 'invoices':
      case 'invoices-all':
        return <InvoiceManagement onNavigate={handleMenuClick} />;
      case 'create-invoice':
        return <InvoiceManagement defaultMode="create" onNavigate={handleMenuClick} />;
      case 'edit-invoice':
        return <InvoiceManagement defaultMode="edit" onNavigate={handleMenuClick} />;
      case 'view-invoice':
        return <InvoiceManagement defaultMode="view" onNavigate={handleMenuClick} />;
      case 'new-invoice':
        return <InvoiceManagement defaultMode="create" onNavigate={handleMenuClick} />;
      case 'pending-invoices':
        return <InvoiceManagement defaultStatus="SENT" onNavigate={handleMenuClick} />;
      case 'approved-invoices':
        return <InvoiceManagement defaultStatus="PAID" onNavigate={handleMenuClick} />;
      case 'statement':
      case 'customer-statement':
        return <InvoiceManagement onNavigate={handleMenuClick} isStatementView={true} />;
      case 'reports':
      case 'report':
          return <Reports defaultStatus="report" onNavigate={handleMenuClick}/>;
      case 'profit-loss':
          return <ProfitLossReport defaultStatus="profit-loss" onNavigate={handleMenuClick}/>;

      case 'settings':
        return <GenericPage title="System Settings" description="Configure system preferences" />;
      default:
        return <Dashboard setCurrentPage={handleMenuClick} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar 
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        currentPage={currentPage}
        expandedMenus={expandedMenus}
        handleMenuClick={handleMenuClick}
      />
      
      <div className="flex flex-col min-h-screen">
        <Header 
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          setCurrentPage={handleMenuClick}
        />
        
        <main className={`
          flex-1 p-4 lg:p-6 transition-all duration-300 
          ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'}
          min-h-screen mt-16
        `}>
          <div className="max-w-full overflow-x-hidden">
            {renderPage()}
          </div>
        </main>
      </div>
    </div>
  );
};

// Protected Route Wrapper
const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return isAuthenticated ? <DashboardLayout /> : <AuthFlow />;
};

// Main App Component
const App = () => {
  return (
    <AuthProvider>
      <div className="App">
        <AppContent />
      </div>
    </AuthProvider>
  );
};

export default App;